import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { view, i18next } from '@yola/ws-sdk';
import bowser from 'yola-bowser';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import useEditorBreakpoint from 'src/js/modules/utils/custom-hooks/use-editor-breakpoint';
import getBreakpointForCurrentDevice from 'src/js/modules/breakpoint-editing/helpers/get-breakpoint-for-current-device';
import {
  breakpointKeys,
  breakpointKeyByPrefix,
  breakpointPrefixByKey,
} from '../constants/breakpoints';

const DESKTOP_VIEW_BREAKPOINT = '1200px';

const BREAKPOINT_KEY_TO_DEVICE_MODE = {
  [breakpointKeys.DEFAULT]: view.deviceModes.DESKTOP,
  [breakpointKeys.MAX_XL]: view.deviceModes.TABLET,
  [breakpointKeys.MAX_MD]: view.deviceModes.MOBILE,
};

const isTouchableDevice =
  bowser.mobile || bowser.tablet || bowser.iPadOS || (bowser.macOS && navigator.maxTouchPoints > 1);

const useDeviceModes = () => {
  const dispatch = useDispatch();
  const isDesktopBreakpointMatched = useEditorBreakpoint(DESKTOP_VIEW_BREAKPOINT, {
    shouldMatchOnMount: true,
  });
  const [featureFlags] = useFeatureFlags(['breakpoint_editing']);
  const { breakpoint_editing: isBreakpointEditingFeatureFlagEnabled } = featureFlags;

  // Subscribe so this hook re-renders when the switcher changes device mode.
  useSelector(view.selectors.getDeviceMode);

  const isDeviceModeSwitcherVisible = Boolean(
    isDesktopBreakpointMatched && isBreakpointEditingFeatureFlagEnabled && !isTouchableDevice
  );

  const activeBreakpointKey = isBreakpointEditingFeatureFlagEnabled
    ? (breakpointKeyByPrefix[getBreakpointForCurrentDevice()] ?? breakpointKeys.DEFAULT)
    : breakpointKeys.DEFAULT;
  const activeBreakpointPrefix = breakpointPrefixByKey[activeBreakpointKey] ?? null;
  const activeDeviceMode = isBreakpointEditingFeatureFlagEnabled
    ? BREAKPOINT_KEY_TO_DEVICE_MODE[activeBreakpointKey]
    : null;

  const setDeviceMode = useCallback(
    (mode) => {
      if (!isDeviceModeSwitcherVisible) return;

      dispatch(view.actions.setDeviceMode(mode));
    },
    [dispatch, isDeviceModeSwitcherVisible]
  );

  const deviceModeOptions = useMemo(
    () => [
      {
        mode: view.deviceModes.DESKTOP,
        glyph: 'monitor',
        label: i18next.t('Desktop'),
        popoverTitle: i18next.t('Desktop (Base)'),
        popoverDescription: i18next.t(
          'Changes apply to all screen sizes unless you adjust Tablet or Mobile.'
        ),
      },
      {
        mode: view.deviceModes.TABLET,
        glyph: 'tablet',
        label: i18next.t('Tablet'),
        popoverTitle: i18next.t('Tablet (1199px and below)'),
        popoverDescription: i18next.t('Changes apply to Tablet and Mobile only.'),
      },
      {
        mode: view.deviceModes.MOBILE,
        glyph: 'smartphone',
        label: i18next.t('Mobile'),
        popoverTitle: i18next.t('Mobile (360px and below)'),
        popoverDescription: i18next.t('Changes apply to Mobile only.'),
      },
    ],
    []
  );

  return {
    activeDeviceMode,
    activeBreakpointKey,
    activeBreakpointPrefix,
    deviceModeOptions,
    setDeviceMode,
    isDeviceModeSwitcherVisible,
  };
};

export default useDeviceModes;
